import {
  R6_CDC,
  R6FrameBuffer,
  frames,
  isValidFrame,
} from "./cyberboardR6CdcProtocol";
import { buildR6Download } from "./cyberboardR6Json";
import {
  parseR6MacroFrames,
  parseR6PressHoldFrames,
} from "./cyberboardR6Upload";

const wait = (milliseconds) =>
  new Promise((resolve) => setTimeout(resolve, milliseconds));

function decodeProductText(response) {
  const length = response[2];
  if (!length || length > R6_CDC.crcIndex - 3) {
    throw new Error("CDC 产品信息长度无效");
  }
  const text = new TextDecoder("utf-8")
    .decode(response.slice(3, 3 + length))
    .replace(/\0+$/, "")
    .trim();
  if (!text || /[^\x20-\x7e]/.test(text)) {
    throw new Error("CDC 产品信息内容无效");
  }
  return text;
}

function frameStatus(response) {
  return response && response.length > 2 ? response[2] : 0;
}

function isCandidatePort(port) {
  const info = port.getInfo ? port.getInfo() : {};
  return R6_CDC.usbFilters.some(
    (filter) =>
      info.usbVendorId === filter.usbVendorId &&
      info.usbProductId === filter.usbProductId
  );
}

function frameCommand(frame, command, subcommand) {
  return frame && frame[0] === command && frame[1] === subcommand;
}

function frameLabel(frame) {
  return [frame[0], frame[1]]
    .map((value) => value.toString(16).padStart(2, "0").toUpperCase())
    .join(" ");
}

function frameHex(frame) {
  return Array.from(frame, (value) =>
    value.toString(16).padStart(2, "0").toUpperCase()
  ).join(" ");
}

function portLabel(port) {
  // getInfo() exposes VID/PID, not the chooser's product-name string.
  if (!port || typeof port.getInfo !== "function") return "CDC 设备";
  const info = port.getInfo();
  if (info.usbVendorId === undefined || info.usbProductId === undefined) {
    return "CDC 设备";
  }
  return `CDC 设备 VID_${info.usbVendorId
    .toString(16)
    .padStart(4, "0")}/PID_${info.usbProductId.toString(16).padStart(4, "0")}`;
}

function portOpenError(port, error) {
  const reason = error && error.message ? error.message : String(error);
  return new Error(
    `R6 CDC 无法打开${portLabel(
      port
    )}，请关闭占用该串口的上位机后重试：${reason}`
  );
}

function releaseLock(lock, name) {
  if (!lock || typeof lock.releaseLock !== "function") return;
  try {
    lock.releaseLock();
  } catch (error) {
    // 断开和超时可能同时清理同一个锁，重复释放不能阻断重连流程。
    console.warn(`R6 CDC ${name} releaseLock failed`, error);
  }
}

function uint32(frame, offset) {
  return (
    frame[offset] * 0x1000000 +
    frame[offset + 1] * 0x10000 +
    frame[offset + 2] * 0x100 +
    frame[offset + 3]
  );
}

function key(bytes, offset) {
  return (
    "#" +
    Array.from(bytes.slice(offset, offset + 4), (item) =>
      item.toString(16).padStart(2, "0")
    )
      .join("")
      .toUpperCase()
  );
}

function parseKeyLayerBytes(bytes, layerCount, keysPerLayer) {
  return Array.from({ length: layerCount }, (_, layerIndex) => ({
    layer: Array.from({ length: keysPerLayer }, (_, keyIndex) =>
      key(bytes, (layerIndex * keysPerLayer + keyIndex) * 4)
    ),
  }));
}

export class CyberboardR6Cdc {
  constructor(options = {}) {
    this.options = {
      baudRate: R6_CDC.baudRate,
      frameTimeout: 2000,
      writeDelay: 5,
      // AM Master uses a 5 ms cadence; writer.ready still provides Web Serial
      // backpressure without imposing an extra 10 ms per frame.
      jsonFrameDelay: 5,
      // Web Serial has no explicit flush; leave the device time to consume
      // the final data frame before sending the 01 06 commit frame.
      jsonFrameTailDelay: 100,
      keyLayerFrameTimeout: 500,
      ...options,
    };
    this.port = null;
    this.reader = null;
    this.writer = null;
    this.frameBuffer = new R6FrameBuffer();
    this.operationChain = Promise.resolve();
    this.operationToken = 0;
    this.jsonDownloadFrameBatch = null;
    this.jsonTransactionBlocked = false;
    this.jsonTransactionUncertain = false;
    this.jsonSessionNeedsReset = false;
    this.handlers = {};
    this.serialDisconnectHandler = (event) => {
      const port = event.port || event.target;
      if (port && port !== this.port) return;
      this.disconnect().catch((error) =>
        console.warn("R6 CDC disconnect handling failed", error)
      );
    };
  }

  get isConnected() {
    return Boolean(this.port && this.port.readable && this.port.writable);
  }

  on(event, handler) {
    if (!this.handlers[event]) this.handlers[event] = [];
    this.handlers[event].push(handler);
  }

  off(event, handler) {
    if (!this.handlers[event]) return;
    this.handlers[event] = handler
      ? this.handlers[event].filter((item) => item !== handler)
      : [];
  }

  emit(event, data) {
    (this.handlers[event] || []).forEach((handler) => {
      try {
        handler(data);
      } catch (error) {
        console.error(`R6 CDC ${event} handler failed`, error);
      }
    });
  }

  isLogEnabled() {
    return (
      typeof this.options.onLog === "function" &&
      (typeof this.options.isLogEnabled !== "function" ||
        this.options.isLogEnabled())
    );
  }

  isVerboseFrameLogEnabled() {
    return (
      typeof this.options.isVerboseFrameLogEnabled === "function" &&
      this.options.isVerboseFrameLogEnabled()
    );
  }

  log(action, detail = {}) {
    if (!this.isLogEnabled()) {
      if (
        action === "json-download:start" ||
        action === "json-download:success" ||
        action === "json-download:uncertain" ||
        action === "json-download:attempt" ||
        action === "json-download:drained" ||
        action === "json-download:rejected" ||
        action === "json-session:reset"
      ) {
        console.info("[R6 CDC]", {
          ...detail,
          time: new Date().toISOString(),
          action,
        });
      }
      return;
    }
    try {
      this.options.onLog({ ...detail, time: new Date().toISOString(), action });
    } catch (error) {
      console.warn("R6 CDC log handler failed", error);
    }
  }

  trace(detail) {
    if (typeof this.options.onTrace !== "function") return;
    try {
      this.options.onTrace(detail);
    } catch (error) {
      console.warn("R6 CDC trace handler failed", error);
    }
  }

  traceFrame(direction, frame) {
    if (!this.isLogEnabled()) return;
    if (this.jsonDownloadFrameBatch && direction === "TX") {
      this.jsonDownloadFrameBatch.frames.push(frame);
      if (
        !this.isVerboseFrameLogEnabled() &&
        (frame[0] === 0x04 || frame[0] === 0x05)
      ) {
        return;
      }
    }
    if (
      !this.isVerboseFrameLogEnabled() &&
      frame[0] === 0x06 &&
      [0x07, 0x08, 0x09].includes(frame[1]) &&
      isValidFrame(frame)
    ) {
      return;
    }
    this.log(direction, {
      command: frameLabel(frame),
      frame: frameHex(frame),
      crcValid: isValidFrame(frame),
    });
  }

  summarizeJsonDownloadFrameBatch(batch, expectedFrameCount) {
    const commandCounts = {};
    const packetGroups = new Map();
    let crcInvalidCount = 0;
    const describe = (frame) => {
      const command = frameLabel(frame);
      const descriptor = { command, crcValid: isValidFrame(frame) };
      if (frame[0] === 0x04 || frame[0] === 0x05) {
        descriptor.pageIndex = frame[1];
        descriptor.frameIndex =
          frame[0] === 0x04 ? frame[2] | (frame[3] << 8) : frame[2];
        descriptor.packetIndex = frame[0] === 0x04 ? frame[4] : frame[3];
      }
      return descriptor;
    };

    batch.frames.forEach((frame) => {
      const command = frameLabel(frame);
      commandCounts[command] = (commandCounts[command] || 0) + 1;
      if (!isValidFrame(frame)) crcInvalidCount += 1;
      if (frame[0] !== 0x04 && frame[0] !== 0x05) return;

      const commandByte = frame[0];
      const frameIndex =
        commandByte === 0x04 ? frame[2] | (frame[3] << 8) : frame[2];
      const packetIndex = commandByte === 0x04 ? frame[4] : frame[3];
      let pages = packetGroups.get(commandByte);
      if (!pages) {
        pages = new Map();
        packetGroups.set(commandByte, pages);
      }
      let logicalFrames = pages.get(frame[1]);
      if (!logicalFrames) {
        logicalFrames = new Map();
        pages.set(frame[1], logicalFrames);
      }
      let packets = logicalFrames.get(frameIndex);
      if (!packets) {
        packets = new Map();
        logicalFrames.set(frameIndex, packets);
      }
      packets.set(packetIndex, (packets.get(packetIndex) || 0) + 1);
    });

    const dataFrames = {};
    packetGroups.forEach((pages, commandByte) => {
      // Protocol-defined RGB splits: page LEDs use 11 packets, key LEDs use 5.
      const expectedPacketCount = commandByte === 0x04 ? 11 : 5;
      const packetIndexes = Array.from(
        { length: expectedPacketCount },
        (_, index) => index
      );
      dataFrames[commandByte.toString(16).padStart(2, "0").toUpperCase()] = {
        expectedPacketsPerFrame: expectedPacketCount,
        pages: Array.from(pages, ([pageIndex, logicalFrames]) => {
          const frameIndexes = Array.from(logicalFrames.keys()).sort(
            (left, right) => left - right
          );
          const incompleteFrames = [];
          let duplicatePacketCount = 0;
          logicalFrames.forEach((packets, frameIndex) => {
            const missingPacketIndexes = packetIndexes.filter(
              (index) => !packets.has(index)
            );
            const unexpectedPacketIndexes = Array.from(packets.keys()).filter(
              (index) => index < 0 || index >= expectedPacketCount
            );
            const duplicateCount = Array.from(packets.values()).reduce(
              (count, occurrences) => count + Math.max(0, occurrences - 1),
              0
            );
            duplicatePacketCount += duplicateCount;
            if (
              missingPacketIndexes.length ||
              unexpectedPacketIndexes.length ||
              duplicateCount
            ) {
              incompleteFrames.push({
                frameIndex,
                receivedPacketCount: packets.size,
                missingPacketIndexes,
                unexpectedPacketIndexes,
                duplicateCount,
              });
            }
          });
          return {
            pageIndex,
            logicalFrameCount: logicalFrames.size,
            frameRange: frameIndexes.length
              ? [frameIndexes[0], frameIndexes[frameIndexes.length - 1]]
              : [],
            packetCount: Array.from(logicalFrames.values()).reduce(
              (count, packets) =>
                count +
                Array.from(packets.values()).reduce((sum, n) => sum + n, 0),
              0
            ),
            incompleteFrameCount: incompleteFrames.length,
            incompleteFrames: incompleteFrames.slice(0, 10),
            duplicatePacketCount,
          };
        }).sort((left, right) => left.pageIndex - right.pageIndex),
      };
    });

    return {
      expectedFrameCount,
      sentFrameCount: batch.frames.length,
      allFramesSent: batch.frames.length === expectedFrameCount,
      commandCounts,
      dataFrames,
      crcInvalidCount,
      firstFrame: batch.frames.length ? describe(batch.frames[0]) : null,
      lastFrame: batch.frames.length
        ? describe(batch.frames[batch.frames.length - 1])
        : null,
      failedFrame: batch.failedFrame ? describe(batch.failedFrame) : null,
    };
  }

  assertSupported() {
    if (!navigator.serial) {
      throw new Error("当前浏览器不支持 Web Serial CDC，请使用 Chrome 或 Edge");
    }
  }

  async requestPort() {
    this.assertSupported();
    return navigator.serial.requestPort({ filters: R6_CDC.usbFilters });
  }

  async connect({ acceptProduct } = {}) {
    if (this.isConnected) return this.port;
    const port = await this.requestPort();
    try {
      await this.open(port);
      if (acceptProduct) {
        const productId = await this.queryProductId();
        if (!acceptProduct(productId)) {
          throw new Error(`CDC 设备型号不匹配，检测到 ${productId}`);
        }
      }
      return port;
    } catch (error) {
      await this.disconnect();
      throw error;
    }
  }

  async connectExisting({ acceptProduct } = {}) {
    this.assertSupported();
    if (this.isConnected) return this.port;
    const ports = await navigator.serial.getPorts();
    for (const port of ports.filter(isCandidatePort)) {
      try {
        const openedPort = await this.open(port);
        if (typeof acceptProduct === "function") {
          const productId = await this.queryProductId();
          if (!acceptProduct(productId)) {
            this.log("connectExisting:unsupported-product", {
              port: portLabel(port),
              productId,
            });
            await this.disconnect();
            continue;
          }
        }
        return openedPort;
      } catch (error) {
        // 已授权端口可能已被拔出或被其它程序占用，继续尝试其它 R6 端口。
        this.log("connectExisting:skip", {
          port: portLabel(port),
          error: error.message,
        });
        await this.disconnect();
      }
    }
    return null;
  }

  async open(port) {
    if (!port) throw new Error("没有可用的 CDC 设备");
    if (!isCandidatePort(port))
      throw new Error("CDC 设备不在 USB 候选白名单中");
    this.log("connect:start", { port: portLabel(port) });
    if (this.port && this.port !== port) await this.disconnect();
    if (!port.readable || !port.writable) {
      try {
        await port.open({
          baudRate: this.options.baudRate,
          bufferSize: 128,
        });
      } catch (error) {
        throw portOpenError(port, error);
      }
    }
    // R6 固件要求 CDC 的 DTR 信号有效后才会响应协议帧。
    if (typeof port.setSignals === "function") {
      try {
        await port.setSignals({ dataTerminalReady: true });
      } catch (error) {
        try {
          await port.close();
        } catch (closeError) {
          console.warn("R6 CDC port cleanup failed", closeError);
        }
        throw portOpenError(port, error);
      }
    }
    this.port = port;
    this.frameBuffer.clear();
    if (typeof port.addEventListener === "function") {
      port.addEventListener("disconnect", this.serialDisconnectHandler);
    }
    if (
      typeof navigator !== "undefined" &&
      typeof navigator.serial?.addEventListener === "function"
    ) {
      navigator.serial.addEventListener(
        "disconnect",
        this.serialDisconnectHandler
      );
    }
    this.emit("connected", port);
    this.log("connect:success", { port: portLabel(port) });
    return port;
  }

  async disconnect() {
    this.log("disconnect:start");
    this.operationToken += 1;
    this.operationChain = Promise.resolve();
    this.clearJsonTransactionState();
    this.jsonSessionNeedsReset = false;
    const port = this.port;
    const reader = this.reader;
    this.port = null;
    if (port && typeof port.removeEventListener === "function") {
      port.removeEventListener("disconnect", this.serialDisconnectHandler);
    }
    if (
      typeof navigator !== "undefined" &&
      typeof navigator.serial?.removeEventListener === "function"
    ) {
      navigator.serial.removeEventListener(
        "disconnect",
        this.serialDisconnectHandler
      );
    }
    try {
      if (reader) await reader.cancel();
    } catch (error) {
      console.warn("R6 CDC reader cancel failed", error);
    } finally {
      if (this.reader === reader) {
        releaseLock(reader, "reader");
        this.reader = null;
      }
    }
    const writer = this.writer;
    releaseLock(writer, "writer");
    if (this.writer === writer) this.writer = null;
    this.frameBuffer.clear();
    if (port) {
      try {
        await port.close();
      } catch (error) {
        console.warn("R6 CDC port close failed", error);
      }
    }
    this.emit("disconnected");
    this.log("disconnect:complete");
  }

  async write(frame, delay = this.options.writeDelay, batchWriter = null) {
    if (!this.isConnected) throw new Error("R6 CDC 尚未连接");
    if (frame.length !== R6_CDC.frameSize) {
      throw new Error("R6 CDC 命令必须是 64 字节");
    }
    if (delay > 0) await wait(delay);
    const ownsWriter = !batchWriter;
    const writer = batchWriter || this.port.writable.getWriter();
    if (ownsWriter) this.writer = writer;
    try {
      if (writer.ready) await writer.ready;
      await writer.write(frame);
      this.traceFrame("TX", frame);
    } catch (error) {
      if (this.jsonDownloadFrameBatch) {
        this.jsonDownloadFrameBatch.failedFrame = frame;
      }
      this.log("TX:failed", {
        command: frameLabel(frame),
        error: error.message,
      });
      throw error;
    } finally {
      if (ownsWriter && this.writer === writer) {
        releaseLock(writer, "writer");
        this.writer = null;
      }
    }
  }

  enqueue(operation) {
    const token = this.operationToken;
    const task = this.operationChain.then(() => {
      if (token !== this.operationToken) {
        throw new Error("R6 CDC 操作已因断开连接取消");
      }
      return operation();
    });
    this.operationChain = task.catch(() => undefined);
    return task;
  }

  async readFrame(
    timeout = this.options.frameTimeout,
    { logTimeout = true } = {}
  ) {
    const buffered = this.frameBuffer.take();
    if (buffered) {
      this.traceFrame("RX", buffered);
      return buffered;
    }
    if (!this.port || !this.port.readable) {
      throw new Error("R6 CDC 不可读取");
    }

    const reader = this.port.readable.getReader();
    this.reader = reader;
    try {
      const deadline = Date.now() + timeout;
      while (Date.now() < deadline) {
        const remaining = deadline - Date.now();
        const result = await this.readWithTimeout(remaining, reader);
        if (result.done) break;
        if (result.value) this.frameBuffer.append(result.value);
        const frame = this.frameBuffer.take();
        if (frame) {
          this.traceFrame("RX", frame);
          return frame;
        }
      }
    } finally {
      if (this.reader === reader) {
        releaseLock(reader, "reader");
        this.reader = null;
      }
    }
    if (logTimeout) this.log("RX:timeout", { timeout });
    throw new Error("等待 R6 CDC 响应超时");
  }

  async readWithTimeout(timeout, reader = this.reader) {
    let timer;
    let timedOut = false;
    const readPromise = reader.read();
    const timeoutPromise = new Promise((resolve) => {
      timer = setTimeout(() => {
        timedOut = true;
        releaseLock(reader, "reader-timeout");
        if (this.reader === reader) this.reader = null;
        resolve({ done: true });
      }, timeout);
    });
    const result = await Promise.race([readPromise, timeoutPromise]).catch(
      (error) => {
        if (timedOut) return { done: true };
        throw error;
      }
    );
    clearTimeout(timer);
    if (timedOut) await readPromise.catch(() => undefined);
    return result;
  }

  requestNow(frame, matchesResponse, timeout = this.options.frameTimeout) {
    return (async () => {
      const command = frameLabel(frame);
      this.log("request:start", { command });
      try {
        await this.write(frame);
        const deadline = Date.now() + timeout;
        let response;
        do {
          const remaining = deadline - Date.now();
          if (remaining <= 0) throw new Error("等待 R6 CDC 响应超时");
          response = await this.readFrame(remaining);
          if (!isValidFrame(response)) {
            throw new Error("R6 CDC 响应 CRC 校验失败");
          }
          if (matchesResponse && !matchesResponse(response)) {
            this.log("request:unmatched-response", {
              command,
              responseCommand: frameLabel(response),
            });
          }
        } while (matchesResponse && !matchesResponse(response));

        if (!isValidFrame(response)) {
          throw new Error("R6 CDC 响应 CRC 校验失败");
        }
        this.log("request:complete", {
          command,
          responseCommand: frameLabel(response),
          status: frameStatus(response),
        });
        return response;
      } catch (error) {
        this.log("request:failed", { command, error: error.message });
        if (error.message === "等待 R6 CDC 响应超时") {
          throw new Error(`等待 R6 CDC 命令 ${frameLabel(frame)} 响应超时`);
        }
        throw error;
      }
    })();
  }

  request(frame, matchesResponse, timeout) {
    return this.enqueue(() => this.requestNow(frame, matchesResponse, timeout));
  }

  async queryProductId() {
    const response = await this.request(frames.productId(), (frame) =>
      frameCommand(frame, 0x01, 0x01)
    );
    return decodeProductText(response);
  }

  async queryProductInfo() {
    const response = await this.request(frames.productInfo(), (frame) =>
      frameCommand(frame, 0x01, 0x02)
    );
    return decodeProductText(response);
  }

  async syncTime() {
    return this.enqueue(async () => {
      const date = new Date();
      const frame = frames.setTime(date);
      this.log("time-sync:start", {
        timestamp: uint32(frame, 2),
        timezoneOffsetMinutes: -date.getTimezoneOffset(),
        timezoneDirection: frame[6],
        timezoneHours: frame[7],
      });
      const response = await this.requestNow(
        frame,
        (candidate) => frameCommand(candidate, 0x01, 0x03),
        Math.min(this.options.frameTimeout, 1000)
      );
      if (frameStatus(response) !== R6_CDC.status.success) {
        throw new Error(`R6 CDC 时间同步失败，状态码 ${frameStatus(response)}`);
      }
      this.log("time-sync:complete", { status: frameStatus(response) });
      return response;
    });
  }

  async queryPageCount() {
    const response = await this.request(frames.pageCount(), (frame) =>
      frameCommand(frame, 0x02, 0x06)
    );
    return response[2];
  }

  async readKeyLayer(layerCount = 7, keysPerLayer = 200) {
    return this.enqueue(async () => {
      this.log("readKeyLayer:start", { layerCount, keysPerLayer });
      const expectedBytes = layerCount * keysPerLayer * 4;
      const packets = new Map();
      const packetPayloads = new Map();
      await this.write(frames.legacyKeyLayerRequest());

      while (packets.size * (R6_CDC.crcIndex - 3) < expectedBytes) {
        let response;
        try {
          response = await this.readFrame(this.options.keyLayerFrameTimeout);
        } catch (error) {
          if (error.message === "等待 R6 CDC 响应超时") break;
          throw error;
        }
        if (!isValidFrame(response)) {
          throw new Error("R6 CDC 键层回读 CRC 校验失败");
        }
        if (!frameCommand(response, 0x06, 0x09)) continue;
        const packetIndex = response[2];
        if (packets.has(packetIndex)) {
          throw new Error(`R6 CDC 键层回读重复分包 ${packetIndex}`);
        }
        const payload = Array.from(response.slice(3, R6_CDC.crcIndex));
        packets.set(packetIndex, payload);
        packetPayloads.set(packetIndex, payload);
      }

      const expectedPackets = Math.ceil(expectedBytes / (R6_CDC.crcIndex - 3));
      const indexes = Array.from(packets.keys()).sort(
        (left, right) => left - right
      );
      if (
        indexes.length !== expectedPackets ||
        indexes.some((index, position) => index !== position)
      ) {
        throw new Error(
          `R6 CDC 键层回读分包不完整，期望 ${expectedPackets} 包`
        );
      }
      const bytes = indexes.flatMap((index) => packets.get(index));
      const layerData = parseKeyLayerBytes(bytes, layerCount, keysPerLayer);
      if (this.options.onTrace) {
        const keyCount = bytes.length / 4;
        const keyValues = Array.from({ length: keyCount }, (_, index) =>
          key(bytes, index * 4)
        );
        const samples = [0, 1, 50, 51, 75, 76, 131, 135].reduce(
          (result, index) => {
            if (index < keyValues.length) result[index] = keyValues[index];
            return result;
          },
          {}
        );
        this.trace({
          feature: "keyLayer",
          packetCount: packets.size,
          byteCount: bytes.length,
          firstKeys: layerData[0]?.layer?.slice(0, 2) || [],
          firstPacket: packetPayloads.get(0)?.slice(0, 16) || [],
          keySamples: samples,
          allFF: keyValues.filter((value) => value === "#FFFFFFFF").length,
          allZero: keyValues.filter((value) => value === "#00000000").length,
          nonEmpty: keyValues.filter(
            (value) => value !== "#FFFFFFFF" && value !== "#00000000"
          ).length,
        });
      }
      this.log("readKeyLayer:complete", {
        packetCount: packets.size,
        byteCount: bytes.length,
        layerCount: layerData.length,
      });
      return layerData;
    });
  }

  async readMacro() {
    return this.enqueue(async () => {
      this.log("readMacro:start", { command: "06 0A" });
      const responses = await this.readLegacyFrames(
        frames.legacyMacroRequest(),
        0x06,
        0x0a,
        (frame) => frame[2],
        (frame) => frame[3]
      );
      // 06 0A 使用旧版“键值 + 间隔”交错布局，不等同于 A6 05 JSON 上传帧。
      const data = parseR6MacroFrames(responses, {}, { legacy: true });
      if (this.options.onTrace) {
        this.trace({
          feature: "macroReadback",
          command: "06 0A",
          packetCount: responses.length,
          macroCount: data.MACRO_key_num,
          frameHeaders: responses.map((frame) =>
            Array.from(frame.slice(0, 10))
          ),
        });
      }
      this.log("readMacro:complete", {
        command: "06 0A",
        packetCount: responses.length,
        macroCount: data.MACRO_key_num,
      });
      return data;
    });
  }

  async readPressHold(baseData) {
    return this.enqueue(async () => {
      this.log("readPressHold:start", { command: "06 0E" });
      const responses = await this.readLegacyFrames(
        frames.legacyPressHoldRequest(),
        0x06,
        0x0e,
        (frame) => (frame[2] << 8) | frame[3],
        (frame) => frame[5]
      );
      const data = parseR6PressHoldFrames(responses, baseData);
      if (this.options.onTrace) {
        this.trace({
          feature: "pressHoldReadback",
          command: "06 0E",
          packetCount: responses.length,
          itemCount: data.tab_key_num,
          frameHeaders: responses.map((frame) =>
            Array.from(frame.slice(0, 12))
          ),
        });
      }
      this.log("readPressHold:complete", {
        command: "06 0E",
        packetCount: responses.length,
        itemCount: data.tab_key_num,
      });
      return data;
    });
  }

  async readLegacyFrames(
    requestFrame,
    command,
    subcommand,
    getExpected,
    getPacketIndex
  ) {
    const responses = [];
    const packetIndexes = new Set();
    await this.write(requestFrame);
    let expected = 0;
    let endReason = "packet-count";
    while (!expected || responses.length < expected) {
      let response;
      try {
        response = await this.readFrame(this.options.keyLayerFrameTimeout, {
          logTimeout: responses.length === 0 || expected > 0,
        });
      } catch (error) {
        if (error.message !== "等待 R6 CDC 响应超时") throw error;
        if (responses.length > 0 && expected === 0) {
          endReason = "idle-timeout";
        }
        break;
      }
      if (!isValidFrame(response)) {
        throw new Error("R6 CDC 独立回读 CRC 校验失败");
      }
      if (!frameCommand(response, command, subcommand)) continue;
      const packetIndex = getPacketIndex
        ? Number(getPacketIndex(response))
        : responses.length;
      if (packetIndexes.has(packetIndex)) {
        throw new Error(
          `R6 CDC ${frameLabel(requestFrame)} 重复分包 ${packetIndex}`
        );
      }
      packetIndexes.add(packetIndex);
      responses.push(response);
      expected = Number(getExpected(response)) || 0;
    }
    if (!responses.length) {
      throw new Error(`R6 CDC 命令 ${frameLabel(requestFrame)} 无回读响应`);
    }
    const indexes = Array.from(packetIndexes).sort(
      (left, right) => left - right
    );
    // 06 0E returns stored rule slots, not consecutive packet indexes.
    const usesRuleSlots = command === 0x06 && subcommand === 0x0e;
    if (
      expected &&
      (responses.length < expected ||
        (!usesRuleSlots &&
          indexes.some((index, position) => index !== position)))
    ) {
      throw new Error(`R6 CDC 命令 ${frameLabel(requestFrame)} 回读不完整`);
    }
    this.log("readback:complete", {
      command: frameLabel(requestFrame),
      packetCount: responses.length,
      expectedPacketCount: expected,
      packetIndexes: indexes,
      endReason,
    });
    return responses;
  }

  async identify() {
    // 先确认产品型号，再查询其它元数据，避免旧固件因不支持后续命令而超时。
    const productId = await this.queryProductId();
    const [productInfo, pageCount] = await Promise.all([
      this.queryProductInfo(),
      this.queryPageCount(),
    ]);
    return { productId, productInfo, pageCount };
  }

  async enterUpgradeMode() {
    const response = await this.request(
      frames.enterUpgradeMode(),
      (frame) => frameCommand(frame, 0x01, 0x04),
      3000
    );
    if (frameStatus(response) !== 1) {
      throw new Error("R6 CDC 设备拒绝进入升级模式");
    }
    this.log("firmware-upgrade:accepted", { command: "01 04" });
    return response;
  }

  async sendKeyChange(change) {
    return this.sendKeyChanges([change]);
  }

  async sendKeyChanges(changes, onProgress) {
    return this.enqueue(async () => {
      await this.prepareCommandSession();
      this.log("sendKeyChanges:start", { count: changes.length });
      for (let index = 0; index < changes.length; index += 1) {
        const frame = frames.changeKey(changes[index]);
        // AM Master defines 06 0B as a write-only command with no response.
        await this.write(frame);
        if (onProgress) onProgress(index + 1, changes.length);
      }
      this.log("sendKeyChanges:success", {
        count: changes.length,
      });
      return;
    });
  }

  // 06 05 is a write-only command in the device protocol.
  async sendMacroFrames(protocolFrames) {
    return this.enqueue(async () => {
      await this.prepareCommandSession();
      this.log("sendMacroFrames:start", { frameCount: protocolFrames.length });
      for (let index = 0; index < protocolFrames.length; index += 1) {
        const frame = protocolFrames[index];
        await this.write(frame);
      }
      this.log("sendMacroFrames:success", {
        frameCount: protocolFrames.length,
      });
    });
  }

  // 06 0D is a write-only command in the device protocol.
  async sendPressHoldFrames(protocolFrames) {
    return this.enqueue(async () => {
      await this.prepareCommandSession();
      this.log("sendPressHoldFrames:start", {
        frameCount: protocolFrames.length,
      });
      for (const frame of protocolFrames) {
        await this.write(frame);
      }
      this.log("sendPressHoldFrames:success", {
        frameCount: protocolFrames.length,
      });
    });
  }

  async sendKeyLayerFrames(protocolFrames, layerCount, onProgress) {
    return this.enqueue(async () => {
      await this.prepareCommandSession();
      const startedAt = Date.now();
      this.log("sendKeyLayerFrames:start", {
        frameCount: protocolFrames.length,
        layerCount,
        frameDelayMs: this.options.jsonFrameDelay,
        rawFrameTraceEnabled: this.isVerboseFrameLogEnabled(),
      });
      // AM Master writes 06 08/06 07 without per-frame ACK. The bridge does
      // not enable a 06 09 check after ordinary incremental writes.
      for (let index = 0; index < protocolFrames.length; index += 1) {
        await this.write(protocolFrames[index], this.options.jsonFrameDelay);
        if (onProgress) onProgress(index + 1, protocolFrames.length, "sending");
      }
      this.log("sendKeyLayerFrames:success", {
        frameCount: protocolFrames.length,
        layerCount,
        elapsedMs: Date.now() - startedAt,
      });
    });
  }

  async sendKeyFunctionFrames(protocolFrames) {
    return this.enqueue(async () => {
      await this.prepareCommandSession();
      this.log("sendKeyFunctionFrames:start", {
        frameCount: protocolFrames.length,
      });
      for (const frame of protocolFrames) {
        await this.write(frame);
      }
      this.log("sendKeyFunctionFrames:success", {
        frameCount: protocolFrames.length,
      });
    });
  }

  async sendValidDirectives(pageCount, validBits) {
    return this.enqueue(() =>
      this.write(frames.validDirectives(pageCount, validBits))
    );
  }

  async sendJsonHandshakeNow(frameCount) {
    const start = await this.requestNow(frames.jsonStart());
    if (
      !frameCommand(start, 0x01, 0x05) ||
      frameStatus(start) !== R6_CDC.status.success
    ) {
      throw new Error(`JSON 开始握手失败，状态码 ${frameStatus(start)}`);
    }
    return this.finishJsonDownload(frameCount);
  }

  async sendJsonHandshake(frameCount) {
    return this.enqueue(() => this.sendJsonHandshakeNow(frameCount));
  }

  async resetJsonSession() {
    const port = this.port;
    if (!port) throw new Error("R6 CDC 尚未连接");
    this.frameBuffer.clear();
    if (typeof port.close === "function" && (port.readable || port.writable)) {
      await port.close();
      await wait(50);
      await port.open({
        baudRate: this.options.baudRate,
        bufferSize: 128,
      });
    }
    if (typeof port.setSignals === "function") {
      await port.setSignals({ dataTerminalReady: true });
    }
    this.frameBuffer.clear();
    await wait(50);
    this.log("json-session:reset");
  }

  async prepareCommandSession() {
    if (!this.jsonSessionNeedsReset) return false;
    await this.resetJsonSession();
    this.jsonSessionNeedsReset = false;
    return true;
  }

  async sendJsonConfig(data, onProgress, features) {
    return this.enqueue(async () => {
      const config = buildR6Download(data, features);
      if (this.jsonTransactionUncertain) {
        throw new Error(
          "上次 JSON 同步已中断，设备状态待确认，请重新连接设备后再试"
        );
      }
      if (onProgress) onProgress(0, config.frames.length, "preparing");
      if (this.jsonSessionNeedsReset) {
        await this.resetJsonSession();
        this.jsonSessionNeedsReset = false;
      }
      const devicePageCount = await this.queryPageCountNow();
      this.log("json-download:start", {
        implementation: "r6-master-partial-v2",
        features: features || "all",
        partialImport: Boolean(data?.__r6PartialImport),
        ignoredSectionCount: Array.isArray(data?.page_data)
          ? data.page_data.reduce((count, page) => {
              return (
                count +
                ["valid", "word_page", "frames", "keyframes"].filter(
                  (section) => {
                    const value =
                      section === "valid"
                        ? page?.valid
                        : page?.[section]?.valid;
                    return value === 2 || value === "2";
                  }
                ).length
              );
            }, 0)
          : 0,
        pageCount: config.pageCount || devicePageCount,
        profilePageCount: config.pageCount,
        devicePageCount,
        frameCount: config.frames.length,
        validBitCount: config.validBits.length,
        frameDelay: this.options.jsonFrameDelay,
        largeTransaction: config.frames.length >= 2000,
        commandCounts: config.frames.reduce((counts, frame) => {
          const command = frameLabel(frame);
          counts[command] = (counts[command] || 0) + 1;
          return counts;
        }, {}),
        validBits: config.validBits,
      });
      let transactionStarted = false;
      try {
        const largeTransaction = config.frames.length >= 2000;
        // Large transactions are more sensitive to CDC buffering; use a
        // slightly slower cadence without duplicating the transaction.
        const frameDelay = Math.max(
          this.options.jsonFrameDelay,
          largeTransaction ? 0 : this.options.jsonFrameDelay
        );
        const tailDelay = Math.max(
          this.options.jsonFrameTailDelay,
          largeTransaction ? 250 : this.options.jsonFrameTailDelay
        );
        this.log("json-download:attempt", {
          frameDelay,
          tailDelay,
        });
        await this.sendValidDirectivesNow(
          config.pageCount || devicePageCount,
          config.validBits
        );
        const start = await this.requestNow(frames.jsonStart());
        if (
          !frameCommand(start, 0x01, 0x05) ||
          frameStatus(start) !== R6_CDC.status.success
        ) {
          throw new Error(`JSON 开始握手失败，状态码 ${frameStatus(start)}`);
        }
        transactionStarted = true;
        if (onProgress) onProgress(0, config.frames.length, "sending");
        const frameBatch = this.isLogEnabled()
          ? { frames: [], failedFrame: null }
          : null;
        this.jsonDownloadFrameBatch = frameBatch;
        let batchWriter;
        try {
          batchWriter = this.port?.writable?.getWriter?.() || null;
          if (batchWriter) this.writer = batchWriter;
          for (let index = 0; index < config.frames.length; index += 1) {
            await this.write(config.frames[index], frameDelay, batchWriter);
            if (onProgress) {
              onProgress(index + 1, config.frames.length, "sending");
            }
          }
        } finally {
          if (batchWriter) {
            await batchWriter.ready;
            await wait(tailDelay);
          }
          if (batchWriter && this.writer === batchWriter) {
            releaseLock(batchWriter, "writer");
            this.writer = null;
          }
          this.jsonDownloadFrameBatch = null;
          if (frameBatch) {
            this.log(
              "json-download:frames",
              this.summarizeJsonDownloadFrameBatch(
                frameBatch,
                config.frames.length
              )
            );
          }
        }
        this.log("json-download:drained", {
          frameCount: config.frames.length,
          tailDelay,
        });
        const result = await this.finishJsonDownload(
          config.frames.length,
          onProgress
        );
        if (onProgress) {
          onProgress(config.frames.length, config.frames.length, "complete");
        }
        this.log("json-download:success", {
          frameCount: config.frames.length,
          status: frameStatus(result),
        });
        this.jsonSessionNeedsReset = true;
        return result;
      } catch (error) {
        if (transactionStarted) {
          if (error?.jsonCommitRejected) {
            this.log("json-download:rejected", {
              reason: error.message,
            });
            this.jsonTransactionUncertain = false;
            this.jsonSessionNeedsReset = true;
            throw error;
          }
          this.jsonTransactionUncertain = true;
          const message =
            error && typeof error.message === "string"
              ? error.message
              : String(error);
          this.log("json-download:uncertain", {
            reason: message,
          });
          throw new Error(
            `JSON 同步中断，设备状态待确认：${message}。请重新连接设备后再试`
          );
        }
        throw error;
      }
    });
  }

  clearJsonTransactionBlock() {
    this.clearJsonTransactionState();
  }

  clearJsonTransactionState() {
    this.jsonTransactionBlocked = false;
    this.jsonTransactionUncertain = false;
    this.log("json-download:state-cleared");
  }

  async queryPageCountNow() {
    const response = await this.requestNow(frames.pageCount(), (frame) =>
      frameCommand(frame, 0x02, 0x06)
    );
    if (!frameCommand(response, 0x02, 0x06)) {
      throw new Error("R6 CDC 页面数量响应格式错误");
    }
    return response[2];
  }

  async sendValidDirectivesNow(pageCount, validBits) {
    await this.write(frames.validDirectives(pageCount, validBits));
    const response = await this.readFrame();
    if (!isValidFrame(response) || !frameCommand(response, 0x02, 0x05)) {
      throw new Error("R6 CDC 有效性指令 ACK 格式错误");
    }
    if (frameStatus(response) !== R6_CDC.status.success) {
      throw new Error(
        `R6 CDC 有效性指令下发失败，状态码 ${frameStatus(response)}`
      );
    }
    return response;
  }

  async finishJsonDownload(frameCount, onProgress) {
    const end = await this.requestNow(frames.jsonEnd(frameCount));
    if (!frameCommand(end, 0x01, 0x06)) {
      throw new Error("JSON 结束握手 ACK 格式错误");
    }
    const status = frameStatus(end);
    if (status === R6_CDC.status.success) return end;
    if (status === R6_CDC.status.waiting) {
      this.log("json-download:pending", { frameCount, status });
      return this.readUntilSuccess(frameCount, onProgress, status);
    }
    const error = new Error(`JSON 结束握手失败，状态码 ${status}`);
    error.jsonCommitRejected = true;
    throw error;
  }

  async readUntilSuccess(frameCount = 0, onProgress, initialStatus) {
    // CB04 needs a long device-side commit phase even for small JSON batches.
    const timeout = Math.min(
      120000,
      Math.max(60000, 10000 + Math.max(0, frameCount) * 100)
    );
    const deadline = Date.now() + timeout;
    this.log("json-download:waiting", {
      frameCount,
      timeout,
      initialStatus,
    });
    let lastPendingStatus = initialStatus;
    const reportWaiting = () => {
      const elapsedMs = Math.max(0, Date.now() - (deadline - timeout));
      if (onProgress) {
        onProgress(frameCount, frameCount, "waiting", { elapsedMs });
      }
      return elapsedMs;
    };
    reportWaiting();
    while (Date.now() < deadline) {
      let response;
      try {
        response = await this.readFrame(
          Math.min(
            Math.max(this.options.frameTimeout, 10000),
            deadline - Date.now()
          ),
          { logTimeout: false }
        );
      } catch (error) {
        if (error.message === "等待 R6 CDC 响应超时") {
          const elapsedMs = reportWaiting();
          this.log("json-download:poll", { frameCount, elapsedMs, timeout });
          continue;
        }
        throw error;
      }
      if (!isValidFrame(response)) throw new Error("R6 CDC 响应 CRC 校验失败");
      if (!frameCommand(response, 0x01, 0x06)) continue;
      const status = frameStatus(response);
      if (status === R6_CDC.status.success) {
        this.log("json-download:complete", { frameCount });
        return response;
      }
      if (status !== R6_CDC.status.waiting) {
        const error = new Error(`JSON 结束握手失败，状态码 ${status}`);
        error.jsonCommitRejected = true;
        throw error;
      }
      if (status !== lastPendingStatus) {
        this.log("json-download:pending", {
          frameCount,
          status,
          elapsedMs: reportWaiting(),
        });
        lastPendingStatus = status;
      }
    }
    this.log("json-download:timeout", { frameCount, timeout });
    throw new Error(
      `等待 R6 CDC JSON 完成超时（${Math.ceil(timeout / 1000)} 秒）`
    );
  }
}
